/**
 * The closed Bayer style vocabulary — single source of truth (§4.2, §5.2).
 *
 * Every name here maps to a Named Style in the Bayer Word template.
 * At insert time `insertHtml()` resolves these classes to the template's live definitions,
 * so the inserted document is always correct even when the bubble preview stylesheet
 * below is stale. The CSS values are for the **preview only**; the template owns the
 * truth on insert.
 *
 * The set below is the union of
 *   (a) the template's `qFormat` styles (Word's own "recommended" gallery flag),
 *       minus anything `semiHidden`, and
 *   (b) the styles Scribbler 1.0 actually shipped (proven in use).
 * Class tokens use the form `getHtml()` emits:
 *   Built-ins are `Mso*` (styleId `Normal`→`MsoNormal`),
 *   Bayer custom styles emit as their raw styleId.
 */

/** Named Style for the `<table>` element itself. */
export const TABLE_CLASS = 'MsoNormalTable' as const
/** Named Style for the table caption `<p>` (first row, spans all columns). */
export const TABLE_CAPTION_CLASS = 'MsoCaption' as const
/** Named Style for the table footnote `<p>` (last row, spans all columns). */
export const TABLE_FOOTNOTE_CLASS = 'BayerTableFootnote' as const

/** Table-scoped Named Styles (applied to `<table>` and cell `<p>` elements). */
const TABLE_STYLE_CLASSES = [
  TABLE_CLASS,
  'BayerTableColumnHeadings',
  TABLE_CAPTION_CLASS,
  'BayerTableRowHeadings',
  TABLE_FOOTNOTE_CLASS,
  'BayerTableStyleLeftJustified',
  'BayerTableStyleCentered',
  'BayerTableStyleRightJustified',
] as const

/** Body-paragraph and heading Named Styles (applied to `<p>` / list elements). */
const BODY_STYLE_CLASSES = [
  'MsoNormal',
  'MsoListParagraph',
  'BayerBodyTextFull',
  // NOTE: BayerBodyText / BayerBodyTextIndented are NOT in the current template
  // (only BayerBodyTextFull exists). Kept for backward-compat with 1.0 content;
  // remove once confirmed unused.
  'BayerBodyText',
  'BayerBodyTextIndented',
  'BayerSectionHeading',
  'BayerToTToFLoRHeading',
  'BayerGuidanceNotes',
  'BayerTRDSource',
  'BayerTRDASectionHeading1',
  'BayerTRDASectionHeading2',
  'BayerTRDASectionHeading3',
  'BayerTRDASectionHeading4',
  'BayerTRDASectionHeading5',
  'BayerSASCaption',
  'BayerSASMono',
  'BayerXspace',
] as const

/** Character-level Named Styles (applied to inline `<span>` runs). */
const INLINE_STYLE_CLASSES = ['BayerSuperscript', 'BayerSubscript'] as const

/** The complete closed vocabulary — nothing outside this set is permitted. */
export const BAYER_STYLE_CLASSES: ReadonlySet<string> = new Set<string>([
  ...TABLE_STYLE_CLASSES,
  ...BODY_STYLE_CLASSES,
  ...INLINE_STYLE_CLASSES,
])

/** True when `className` is a recognised Bayer Named Style. */
export function isKnownStyleClass(className: string): boolean {
  return BAYER_STYLE_CLASSES.has(className.trim())
}

/**
 * Case-insensitive lookup map: lowercase class token → canonical class name.
 * Lets the LLM emit e.g. `BayerBodytextFull` and still get the correct
 * `BayerBodyTextFull` selector after normalisation.
 */
const CANONICAL_CLASS_BY_LOWER: ReadonlyMap<string, string> = new Map(
  [...BAYER_STYLE_CLASSES].map((cls) => [cls.toLowerCase(), cls])
)

/**
 * Returns the canonical class name for `className` (case-insensitive), or
 * `null` if `className` is not in the Bayer vocabulary.
 */
export function canonicalStyleClass(className: string): string | null {
  return CANONICAL_CLASS_BY_LOWER.get(className.trim().toLowerCase()) ?? null
}

/** True when `html` contains at least one known Bayer Named Style class. */
export function hasBayerStyleClasses(html: string): boolean {
  return [...BAYER_STYLE_CLASSES].some((cls) => html.includes(cls))
}

/**
 * Raw CSS rules for all table Named Styles — no `<style>` wrapper.
 * Used both in `BAYER_CANONICAL_STYLESHEET` (preview bubble) and in
 * `html-utils.ts` (inserted table HTML sent to Word).
 */
export const TABLE_STYLE_CSS =
  `table.${TABLE_CLASS} { mso-style-name:"Normal Table"; border-collapse:collapse; }\n` +
  `p.${TABLE_CAPTION_CLASS},li.${TABLE_CAPTION_CLASS},div.${TABLE_CAPTION_CLASS} { mso-style-name:"Caption"; margin-top:6.0pt; margin-right:0cm; margin-bottom:6.0pt; margin-left:45.35pt; page-break-after:avoid; font-size:10.0pt; font-family:"Arial",sans-serif; font-weight:bold; }\n` +
  `p.BayerTableColumnHeadings,li.BayerTableColumnHeadings,div.BayerTableColumnHeadings { mso-style-name:"Bayer Table Column Headings"; margin:0cm; text-align:center; font-size:10.0pt; font-family:"Arial",sans-serif; font-weight:bold; }\n` +
  `p.BayerTableRowHeadings,li.BayerTableRowHeadings,div.BayerTableRowHeadings { mso-style-name:"Bayer Table Row Headings"; margin-top:0cm; margin-right:0cm; margin-bottom:0cm; margin-left:11.35pt; font-size:10.0pt; font-family:"Arial",sans-serif; }\n` +
  `p.BayerTableStyleLeftJustified,li.BayerTableStyleLeftJustified,div.BayerTableStyleLeftJustified { mso-style-name:"Bayer TableStyle Left Justified"; margin:0cm; page-break-after:avoid; font-size:10.0pt; font-family:"Arial",sans-serif; }\n` +
  `p.BayerTableStyleCentered,li.BayerTableStyleCentered,div.BayerTableStyleCentered { mso-style-name:"Bayer TableStyle Centered"; margin:0cm; text-align:center; font-size:10.0pt; font-family:"Arial",sans-serif; }\n` +
  `p.BayerTableStyleRightJustified,li.BayerTableStyleRightJustified,div.BayerTableStyleRightJustified { mso-style-name:"Bayer TableStyle Right Justified"; margin:0cm; text-align:right; font-size:10.0pt; font-family:"Arial",sans-serif; }\n` +
  `p.${TABLE_FOOTNOTE_CLASS},li.${TABLE_FOOTNOTE_CLASS},div.${TABLE_FOOTNOTE_CLASS} { mso-style-name:"Bayer Table Footnote"; margin-top:0cm; margin-right:0cm; margin-bottom:0cm; margin-left:18.0pt; text-indent:-18.0pt; page-break-after:avoid; font-size:10.0pt; font-family:"Arial",sans-serif; }`

/**
 * The shared Bayer preview stylesheet — one `<style>` block rendered in the
 * chat bubble so class-based content displays with template-like formatting.
 *
 * Word uses `mso-style-name` to map a CSS class to a Named Style by display name:
 *   class="BayerTableStyleLeftJustified" + this block → Word finds "Bayer TableStyle
 *   Left Justified" in the template and applies it instead of creating a new lowercase
 *   CSS-only fallback class with Times New Roman 12pt.
 */
export const BAYER_CANONICAL_STYLESHEET = `<style>
${TABLE_STYLE_CSS}
p.MsoNormal,li.MsoNormal,div.MsoNormal { mso-style-name:"Normal"; margin:0cm; font-size:12.0pt; font-family:"Times New Roman",serif; }
p.MsoListParagraph,li.MsoListParagraph,div.MsoListParagraph { mso-style-name:"List Paragraph"; margin-top:0cm; margin-right:0cm; margin-bottom:0cm; margin-left:45.35pt; font-size:12.0pt; font-family:"Times New Roman",serif; }
p.BayerBodyTextFull,li.BayerBodyTextFull,div.BayerBodyTextFull { mso-style-name:"Bayer Body Text Full"; margin-top:6.0pt; margin-right:0cm; margin-bottom:6.0pt; margin-left:0cm; font-size:12.0pt; font-family:"Times New Roman",serif; }
p.BayerBodyText,li.BayerBodyText,div.BayerBodyText { mso-style-name:"Bayer Body Text"; margin-top:6.0pt; margin-right:0cm; margin-bottom:6.0pt; margin-left:45.35pt; font-size:12.0pt; font-family:"Times New Roman",serif; }
p.BayerBodyTextIndented,li.BayerBodyTextIndented,div.BayerBodyTextIndented { mso-style-name:"Bayer Body Text Indented"; margin-top:6.0pt; margin-right:0cm; margin-bottom:6.0pt; margin-left:45.35pt; text-indent:18.0pt; font-size:12.0pt; font-family:"Times New Roman",serif; }
p.BayerSectionHeading,li.BayerSectionHeading,div.BayerSectionHeading { mso-style-name:"Bayer Section Heading"; }
p.BayerToTToFLoRHeading,li.BayerToTToFLoRHeading,div.BayerToTToFLoRHeading { mso-style-name:"Bayer ToT ToF LoR Heading"; margin:0cm; font-size:12.0pt; font-family:"Arial",sans-serif; font-weight:bold; }
p.BayerGuidanceNotes,li.BayerGuidanceNotes,div.BayerGuidanceNotes { mso-style-name:"Bayer Guidance Notes"; margin-top:6.0pt; margin-right:0cm; margin-bottom:6.0pt; margin-left:0cm; font-size:12.0pt; font-family:"Times New Roman",serif; font-style:italic; }
p.BayerTRDSource,li.BayerTRDSource,div.BayerTRDSource { mso-style-name:"Bayer TRD Source"; margin-top:3.0pt; margin-right:0cm; margin-bottom:3.0pt; margin-left:0cm; font-size:10.0pt; font-family:"Arial",sans-serif; font-style:italic; }
p.BayerTRDASectionHeading1,li.BayerTRDASectionHeading1,div.BayerTRDASectionHeading1 { mso-style-name:"Bayer TRDA Section Heading 1"; margin-top:12.0pt; margin-right:0cm; margin-bottom:3.0pt; margin-left:0cm; page-break-after:avoid; font-size:14.0pt; font-family:"Arial",sans-serif; font-weight:bold; }
p.BayerTRDASectionHeading2,li.BayerTRDASectionHeading2,div.BayerTRDASectionHeading2 { mso-style-name:"Bayer TRDA Section Heading 2"; margin-top:10.0pt; margin-right:0cm; margin-bottom:3.0pt; margin-left:0cm; page-break-after:avoid; font-size:13.0pt; font-family:"Arial",sans-serif; font-weight:bold; }
p.BayerTRDASectionHeading3,li.BayerTRDASectionHeading3,div.BayerTRDASectionHeading3 { mso-style-name:"Bayer TRDA Section Heading 3"; margin-top:8.0pt; margin-right:0cm; margin-bottom:3.0pt; margin-left:0cm; page-break-after:avoid; font-size:12.0pt; font-family:"Arial",sans-serif; font-weight:bold; }
p.BayerTRDASectionHeading4,li.BayerTRDASectionHeading4,div.BayerTRDASectionHeading4 { mso-style-name:"Bayer TRDA Section Heading 4"; margin-top:6.0pt; margin-right:0cm; margin-bottom:3.0pt; margin-left:0cm; page-break-after:avoid; font-size:12.0pt; font-family:"Arial",sans-serif; font-weight:bold; font-style:italic; }
p.BayerTRDASectionHeading5,li.BayerTRDASectionHeading5,div.BayerTRDASectionHeading5 { mso-style-name:"Bayer TRDA Section Heading 5"; margin-top:6.0pt; margin-right:0cm; margin-bottom:3.0pt; margin-left:0cm; page-break-after:avoid; font-size:12.0pt; font-family:"Arial",sans-serif; font-style:italic; }
p.BayerSASCaption,li.BayerSASCaption,div.BayerSASCaption { mso-style-name:"Bayer SAS Caption"; margin-top:6.0pt; margin-right:0cm; margin-bottom:6.0pt; margin-left:0cm; page-break-after:avoid; font-size:10.0pt; font-family:"Arial",sans-serif; font-weight:bold; }
p.BayerSASMono,li.BayerSASMono,div.BayerSASMono { mso-style-name:"Bayer SAS Mono"; margin:0cm; font-size:9.0pt; font-family:"Courier New",monospace; }
p.BayerXspace,li.BayerXspace,div.BayerXspace { mso-style-name:"Bayer Xspace"; margin:0cm; font-size:6.0pt; font-family:"Times New Roman",serif; }
span.BayerSuperscript { mso-style-name:"Bayer Superscript"; vertical-align:super; font-size:8.0pt; }
span.BayerSubscript { mso-style-name:"Bayer Subscript"; vertical-align:sub; font-size:8.0pt; }
</style>`

/**
 * Prepends `BAYER_CANONICAL_STYLESHEET` to `html` when any Bayer Named Style
 * class is present — shared gate used by the bubble path and both insert paths.
 * Word reads the stylesheet as a diff baseline for inline-style overrides on
 * known Named Styles (§7.1 V2).
 */
export function prependBayerStylesheetIfNeeded(html: string): string {
  return hasBayerStyleClasses(html) ? `${BAYER_CANONICAL_STYLESHEET}${html}` : html
}
